import type {
  OrganicStatus,
  PointSamplingStatus,
  SamplingPeriod,
} from '../../data-access/types/gp-sampling-assignment';
import type {
  AssignmentMapColorMode,
  DrawActionMode,
} from '../../lib/assignment-context';

import type { AssignmentState } from './assignment-map-filters';

export type { AssignmentMapColorMode, DrawActionMode };

export const ASSIGNMENT_STATE_OPTIONS: AssignmentState[] = [
  'unassigned',
  'assigned',
  'sms_active',
  'visible',
  'hidden',
  'confirmed',
];

export const ORGANIC_FILTER_GROUPS: {
  id: string;
  values: OrganicStatus[];
  labelKey: string;
}[] = [
  { id: 'not_asked', values: ['not_asked'], labelKey: 'legendNotAsked' },
  { id: 'pending', values: ['pending'], labelKey: 'legendPending' },
  { id: 'ok', values: ['ok'], labelKey: 'legendOk' },
  { id: 'deferred', values: ['deferred', 'blocked'], labelKey: 'legendDeferred' },
  { id: 'expired', values: ['expired'], labelKey: 'legendExpired' },
];

export const ORGANIC_LEGEND_ITEMS = [
  { color: '#228be6', labelKey: 'legendNotAsked' },
  { color: '#fab005', labelKey: 'legendPending' },
  { color: '#40c057', labelKey: 'legendOk' },
  { color: '#fd7e14', labelKey: 'legendDeferred' },
  { color: '#e03131', labelKey: 'legendExpired' },
] as const;

export const SAMPLING_PERIODS: SamplingPeriod[] = ['spring', 'summer', 'autumn'];

export function buildPeriodSelectOptions(
  i18n: (key: string) => string,
): { value: SamplingPeriod; label: string }[] {
  return SAMPLING_PERIODS.map((period) => ({
    value: period,
    label: i18n(
      period === 'spring' ? 'periodSpring' : period === 'summer' ? 'periodSummer' : 'periodAutumn',
    ),
  }));
}

export const SAMPLING_LEGEND_ITEMS = [
  { color: '#adb5bd', labelKey: 'legendSamplingIncomplete', status: 'INCOMPLETE' },
  { color: '#fab005', labelKey: 'legendSamplingPending', status: 'PENDING' },
  { color: '#40c057', labelKey: 'legendSamplingFinished', status: 'FINISHED' },
  { color: '#e03131', labelKey: 'legendSamplingImpossible', status: 'IMPOSSIBLE' },
] as const;

export function buildColorModeSegments(
  i18n: (key: string) => string,
  options?: { modes?: AssignmentMapColorMode[] },
): { label: string; value: AssignmentMapColorMode }[] {
  const allSegments: { label: string; value: AssignmentMapColorMode; mode: AssignmentMapColorMode }[] = [
    { label: i18n('legendModeSampler'), value: 'sampler', mode: 'sampler' },
    { label: i18n('legendModeOrganic'), value: 'organic', mode: 'organic' },
    { label: i18n('legendModePeriod'), value: 'period', mode: 'period' },
    { label: i18n('legendModeSampling'), value: 'sampling', mode: 'sampling' },
    { label: i18n('legendModeLaboratory'), value: 'laboratory', mode: 'laboratory' },
  ];
  const allowed = options?.modes ?? ['sampler', 'organic', 'period'];
  return allSegments.filter((segment) => allowed.includes(segment.mode));
}

export function legendModeTitleKey(mode: AssignmentMapColorMode): string {
  switch (mode) {
    case 'sampler':
      return 'legendModeSampler';
    case 'period':
      return 'legendModePeriod';
    case 'sampling':
      return 'legendModeSampling';
    case 'laboratory':
      return 'legendModeLaboratory';
    default:
      return 'legendModeOrganic';
  }
}

export function samplingStatusLabelKey(status: string): string {
  switch (status as PointSamplingStatus) {
    case 'FINISHED':
      return 'legendSamplingFinished';
    case 'IMPOSSIBLE':
      return 'legendSamplingImpossible';
    case 'PENDING':
      return 'legendSamplingPending';
    case 'INCOMPLETE':
    default:
      return 'legendSamplingIncomplete';
  }
}

export function organicLabelKey(status: OrganicStatus): string {
  switch (status) {
    case 'not_asked':
      return 'legendNotAsked';
    case 'pending':
      return 'legendPending';
    case 'ok':
      return 'legendOk';
    case 'deferred':
    case 'blocked':
      return 'legendDeferred';
    case 'expired':
      return 'legendExpired';
    default:
      return 'legendNotAsked';
  }
}

export function isOrganicGroupActive(selected: OrganicStatus[], values: OrganicStatus[]): boolean {
  return values.some((value) => selected.includes(value));
}

export function toggleOrganicGroup(
  selected: OrganicStatus[],
  values: OrganicStatus[],
): OrganicStatus[] {
  if (isOrganicGroupActive(selected, values)) {
    return selected.filter((value) => !values.includes(value));
  }
  return [...new Set([...selected, ...values])];
}

export function countActiveOrganicGroups(selected: OrganicStatus[]): number {
  return ORGANIC_FILTER_GROUPS.filter((group) => isOrganicGroupActive(selected, group.values))
    .length;
}
